import { useState } from "react";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { nestRequestPasswordReset, NestApiError } from "@/services/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Forgot password?" step of the email login page: asks for the email and
 * requests a reset link. The confirmation never says whether the email has an
 * account — the API answers the same either way.
 */
export function ForgotPasswordForm({
  initialEmail = "",
  onBack,
}: {
  initialEmail?: string;
  onBack: () => void;
}) {
  const { t } = useLanguage();
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const normalized = email.trim().toLowerCase();
  const isValid = EMAIL_RE.test(normalized);

  const handleSubmit = async () => {
    if (!isValid || isLoading) return;
    setIsLoading(true);
    try {
      await nestRequestPasswordReset(normalized);
      setSentTo(normalized);
    } catch (err: unknown) {
      toast.error(err instanceof NestApiError && err.status === 429
        ? t("محاولات كثيرة، حاول لاحقاً", "Too many attempts. Try again later")
        : t("تعذّر إرسال الرابط، حاول مرة أخرى", "Couldn't send the link, please try again"));
    } finally {
      setIsLoading(false);
    }
  };

  const backButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={onBack}
      className="text-white/70 hover:text-white hover:bg-white/10 -ms-3 self-start"
    >
      <ArrowLeft className="h-4 w-4 me-2 rtl:rotate-180" />
      {t("رجوع", "Back")}
    </Button>
  );

  if (sentTo) {
    return (
      <>
        {backButton}
        <div className="space-y-4 w-full !text-center" data-testid="forgot-password-sent">
          <MailCheck className="h-12 w-12 text-[#791FDA] mx-auto" />
          <h1 className="text-2xl font-bold text-white !text-center">
            {t("تفقّد بريدك الإلكتروني", "Check your email")}
          </h1>
          <p className="text-white/60 text-sm !text-center leading-relaxed">
            {t(
              "إذا كان هناك حساب مرتبط بالبريد",
              "If an account exists for",
            )}{" "}
            <span dir="ltr" className="text-white font-medium">{sentTo}</span>
            {t(
              "، فقد أرسلنا إليه رابطاً لتعيين كلمة مرور جديدة. الرابط صالح لمدة 30 دقيقة.",
              ", we've sent it a link to set a new password. The link is valid for 30 minutes.",
            )}
          </p>
        </div>
        <Button onClick={onBack} className="w-full bg-[#791FDA] hover:bg-[#6a18c7] text-white" size="lg">
          {t("العودة لتسجيل الدخول", "Back to login")}
        </Button>
      </>
    );
  }

  return (
    <>
      {backButton}
      <div className="space-y-2 w-full !text-center">
        <h1 className="text-2xl font-bold text-white !text-center">
          {t("نسيت كلمة المرور؟", "Forgot your password?")}
        </h1>
        <p className="text-white/60 text-sm !text-center">
          {t(
            "أدخل بريدك الإلكتروني وسنرسل لك رابطاً لتعيين كلمة مرور جديدة",
            "Enter your email and we'll send you a link to set a new password",
          )}
        </p>
      </div>

      <div className="space-y-2 w-full">
        <label htmlFor="forgot-password-email" className="text-sm font-medium text-white block text-start">
          {t("البريد الإلكتروني", "Email")}
        </label>
        <Input
          id="forgot-password-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          placeholder="ahmed@example.com"
          dir="ltr"
          disabled={isLoading}
          autoFocus
          className="bg-white/10 border-white/20 text-white placeholder-white/40"
        />
      </div>

      <Button
        onClick={handleSubmit}
        className="w-full bg-[#791FDA] hover:bg-[#6a18c7] text-white"
        disabled={isLoading || !isValid}
        size="lg"
        data-testid="forgot-password-submit"
      >
        {isLoading ? (
          <>
            <Loader2 className="me-2 h-4 w-4 animate-spin" />
            {t("جاري الإرسال...", "Sending...")}
          </>
        ) : (
          t("إرسال رابط إعادة التعيين", "Send reset link")
        )}
      </Button>
    </>
  );
}
